/**
 * @fileoverview Analytics API Contract (Contract-First Design)
 * @module types/contracts
 *
 * Complete API contract for the Analytics module.
 * Single source of truth for all analytics-related API definitions.
 */

import { z } from 'zod';
import { createContract, ApiSuccessResponseSchema } from './contract.utils';

// ============================================================================
// SCHEMAS (Data Structure & Validation)
// ============================================================================

/**
 * Safe experiment identifier pattern.
 * Only lowercase alphanumeric + underscore to prevent injection via JSONB queries.
 */
const safeIdentifier = z
	.string()
	.regex(/^[a-z0-9_]+$/, 'Must be lowercase alphanumeric with underscores')
	.max(100);

/**
 * Analytics Filters Schema
 */
export const AnalyticsFiltersSchema = z.object({
	startDate: z.iso.datetime().optional(),
	endDate: z.iso.datetime().optional(),
	utmSource: z.string().optional(),
	utmCampaign: z.string().optional(),
	utmMedium: z.string().optional(),
	country: z.string().optional(),
	excludeBots: z.boolean().optional(),
	// Experiment filters — restrict analytics to sessions in a specific experiment/variant
	experimentId: safeIdentifier.optional(),
	variantId: safeIdentifier.optional(),
});
export type AnalyticsFilters = z.infer<typeof AnalyticsFiltersSchema>;

/**
 * Experiment Option Schema
 * Returned by the experiments discovery endpoint for dynamic dropdown population.
 */
export const ExperimentOptionSchema = z.object({
	experimentId: z.string(),
	variants: z.array(z.string()),
	totalAssignments: z.number().int().nonnegative(),
});
export type ExperimentOption = z.infer<typeof ExperimentOptionSchema>;

export const ExperimentOptionsResponseSchema = z.array(ExperimentOptionSchema);

/**
 * Business Analytics Overview Schema
 */
export const BusinessAnalyticsOverviewSchema = z.object({
	totalSessions: z.number().int().nonnegative(),
	completedTests: z.number().int().nonnegative(),
	paidConversions: z.number().int().nonnegative(),
	conversionRate: z.number().nonnegative(),
	completionRate: z.number().nonnegative(),
	averageSessionDuration: z.number().nonnegative(),
	totalRevenue: z.number().nonnegative(), // Revenue in cents
	averageOrderValue: z.number().nonnegative(), // AOV in cents
});
export type BusinessAnalyticsOverview = z.infer<typeof BusinessAnalyticsOverviewSchema>;

/**
 * Funnel Analytics Schema
 */
export const FunnelAnalyticsSchema = z.object({
	started: z.number().int().nonnegative(), // Total tracking sessions
	testsStarted: z.number().int().nonnegative(), // Sessions that started a test (have test_started event)
	completed: z.number().int().nonnegative(), // Test sessions with status completed
	converted: z.number().int().nonnegative(), // Sessions with successful payment
	dropoffRates: z.object({
		startToCompletion: z.number().nonnegative(),
		completionToPayment: z.number().nonnegative(),
	}),
});
export type FunnelAnalytics = z.infer<typeof FunnelAnalyticsSchema>;

/**
 * Trend Data Point Schema
 * Represents daily analytics data with session, conversion, and payment metrics
 */
export const TrendDataPointSchema = z.object({
	date: z.string(), // ISO date string (YYYY-MM-DD)
	sessions: z.number().int().nonnegative(),
	conversions: z.number().int().nonnegative(),
	payments: z.number().int().nonnegative().nullable().optional(),
	revenue: z.number().nonnegative().nullable().optional(),
});
export type TrendDataPoint = z.infer<typeof TrendDataPointSchema>;

/**
 * Conversion Source Schema
 */
export const ConversionSourceSchema = z.object({
	source: z.string(),
	conversions: z.number().int().nonnegative(),
	rate: z.number().nonnegative(),
});
export type ConversionSource = z.infer<typeof ConversionSourceSchema>;

/**
 * Payment By Day Schema
 */
export const PaymentByDaySchema = z.object({
	date: z.string(),
	count: z.number().int().nonnegative(),
	revenue: z.number().nonnegative(),
});
export type PaymentByDay = z.infer<typeof PaymentByDaySchema>;

/**
 * Payment By Method Schema
 */
export const PaymentByMethodSchema = z.object({
	method: z.string(),
	count: z.number().int().nonnegative(),
	revenue: z.number().nonnegative(),
});
export type PaymentByMethod = z.infer<typeof PaymentByMethodSchema>;

/**
 * Business Analytics Response Schema
 * Consolidated to include all analytics data in a single response
 * This eliminates the need for multiple redundant API calls
 */
export const BusinessAnalyticsResponseSchema = z.object({
	overview: BusinessAnalyticsOverviewSchema,
	funnel: FunnelAnalyticsSchema,
	trends: z.array(TrendDataPointSchema),
	// Payment analytics (consolidated to avoid redundant API calls)
	paymentsByDay: z.array(PaymentByDaySchema),
	paymentsByMethod: z.array(PaymentByMethodSchema),
	// Conversion analytics (consolidated to avoid redundant API calls)
	topConversionSources: z.array(ConversionSourceSchema),
	averageTimeToConversion: z.number().nonnegative(),
});
export type BusinessAnalyticsResponse = z.infer<typeof BusinessAnalyticsResponseSchema>;

/**
 * Conversion Analytics Response Schema
 */
export const ConversionAnalyticsResponseSchema = z.object({
	totalSessions: z.number().int().nonnegative(),
	completedSessions: z.number().int().nonnegative(),
	convertedSessions: z.number().int().nonnegative(),
	conversionRate: z.number().nonnegative(),
	averageTimeToConversion: z.number().nonnegative(),
	topConversionSources: z.array(ConversionSourceSchema),
});
export type ConversionAnalyticsResponse = z.infer<typeof ConversionAnalyticsResponseSchema>;

/**
 * Payment Analytics Schema
 */
export const PaymentAnalyticsSchema = z.object({
	totalRevenue: z.number().nonnegative(),
	totalPayments: z.number().int().nonnegative(),
	averageOrderValue: z.number().nonnegative(),
	paymentsByDay: z.array(
		z.object({
			date: z.string(),
			count: z.number().int().nonnegative(),
			revenue: z.number().nonnegative(),
		}),
	),
	paymentsByMethod: z.array(
		z.object({
			method: z.string(),
			count: z.number().int().nonnegative(),
			revenue: z.number().nonnegative(),
		}),
	),
});
export type PaymentAnalytics = z.infer<typeof PaymentAnalyticsSchema>;

/**
 * Session Analytics Schema
 */
export const SessionAnalyticsSchema = z.object({
	totalSessions: z.number().int().nonnegative(),
	activeSessions: z.number().int().nonnegative(),
	completedSessions: z.number().int().nonnegative(),
	convertedSessions: z.number().int().nonnegative(),
	abandonedSessions: z.number().int().nonnegative(),
	averageSessionDuration: z.number().nonnegative(),
	conversionRate: z.number().nonnegative(),
	completionRate: z.number().nonnegative(),
});
export type SessionAnalytics = z.infer<typeof SessionAnalyticsSchema>;

/**
 * User Journey Event Schema
 */
export const UserJourneyEventSchema = z.object({
	eventType: z.string(),
	timestamp: z.iso.datetime(),
	timeSinceStart: z.number().nonnegative(),
	metadata: z.record(z.string(), z.unknown()).optional(),
});
export type UserJourneyEvent = z.infer<typeof UserJourneyEventSchema>;

/**
 * User Journey Response Schema
 */
export const UserJourneyResponseSchema = z.object({
	sessionId: z.uuid(),
	events: z.array(UserJourneyEventSchema),
	outcome: z.enum(['abandoned', 'completed', 'converted']),
	totalDuration: z.number().nonnegative(),
});
export type UserJourneyResponse = z.infer<typeof UserJourneyResponseSchema>;

// ============================================================================
// API RESPONSE WRAPPERS
// ============================================================================

export const BusinessAnalyticsApiResponseSchema = ApiSuccessResponseSchema(
	BusinessAnalyticsResponseSchema,
);
export const ConversionAnalyticsApiResponseSchema = ApiSuccessResponseSchema(
	ConversionAnalyticsResponseSchema,
);
export const PaymentAnalyticsApiResponseSchema = ApiSuccessResponseSchema(PaymentAnalyticsSchema);
export const SessionAnalyticsApiResponseSchema = ApiSuccessResponseSchema(SessionAnalyticsSchema);
export const UserJourneyApiResponseSchema = ApiSuccessResponseSchema(UserJourneyResponseSchema);
export const ExperimentOptionsApiResponseSchema = ApiSuccessResponseSchema(
	ExperimentOptionsResponseSchema,
);

// ============================================================================
// API CONTRACT (Routes, Methods, Request/Response)
// ============================================================================

/**
 * Analytics API Contract
 *
 * Defines the complete API surface for the Analytics module.
 * Both frontend and backend use this as the single source of truth.
 */
export const ANALYTICS_API_CONTRACT = createContract('/analytics', {
	/**
	 * GET /analytics/business
	 * Get comprehensive business analytics
	 */
	business: {
		method: 'GET' as const,
		path: '/business',
		query: AnalyticsFiltersSchema.optional(),
		response: BusinessAnalyticsApiResponseSchema,
	},

	/**
	 * GET /analytics/conversion
	 * Get conversion analytics
	 */
	conversion: {
		method: 'GET' as const,
		path: '/conversion',
		query: AnalyticsFiltersSchema.optional(),
		response: ConversionAnalyticsApiResponseSchema,
	},

	/**
	 * GET /analytics/payment
	 * Get payment analytics
	 */
	payment: {
		method: 'GET' as const,
		path: '/payment',
		query: AnalyticsFiltersSchema.optional(),
		response: PaymentAnalyticsApiResponseSchema,
	},

	/**
	 * GET /analytics/session
	 * Get session analytics
	 */
	session: {
		method: 'GET' as const,
		path: '/session',
		query: AnalyticsFiltersSchema.optional(),
		response: SessionAnalyticsApiResponseSchema,
	},

	/**
	 * GET /analytics/journey/:sessionId
	 * Get user journey for a specific session
	 */
	journey: {
		method: 'GET' as const,
		path: '/journey/:sessionId',
		params: z.object({
			sessionId: z.uuid(),
		}),
		response: UserJourneyApiResponseSchema,
	},

	/**
	 * GET /analytics/experiments
	 * List active experiments with their variants (admin-only, for dropdown population)
	 */
	experiments: {
		method: 'GET' as const,
		path: '/experiments',
		response: ExperimentOptionsApiResponseSchema,
	},
});

/**
 * Route Constants for Controllers
 * Use these in @Controller() and @Get/@Post/@Delete decorators
 */
export const ANALYTICS_ROUTES = {
	base: ANALYTICS_API_CONTRACT.base,
	business: ANALYTICS_API_CONTRACT.routes.business.path,
	conversion: ANALYTICS_API_CONTRACT.routes.conversion.path,
	payment: ANALYTICS_API_CONTRACT.routes.payment.path,
	session: ANALYTICS_API_CONTRACT.routes.session.path,
	journey: ANALYTICS_API_CONTRACT.routes.journey.path,
	experiments: ANALYTICS_API_CONTRACT.routes.experiments.path,
};

// ============================================================================
// TYPE EXPORTS
// ============================================================================

/**
 * Export all types for use in frontend and backend
 */
export type AnalyticsApiContract = typeof ANALYTICS_API_CONTRACT;
